import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh,
  Cancel,
  Stop,
  PlayArrow,
  Queue,
  CheckCircle,
  FilterList,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface QueueStatus {
  active_jobs: number;
  queued_jobs: number;
  completed_jobs: number;
  total_capacity: number;
  status: string;
}

interface ServiceStatus {
  serviceUrl: string;
  status: 'online' | 'offline' | 'error';
  data?: QueueStatus;
  error?: string;
}

interface Job {
  job_id: string;
  status: string;
  job_type: string;
  progress: {
    progress: number;
    message: string;
  };
  created_at?: string;
  result?: string;
  error?: string;
}

interface QueueResponse {
  aggregated: QueueStatus;
  services: ServiceStatus[];
  jobs?: Job[];
  timestamp: string;
}

const QueueManagement: React.FC = () => {
  const [queueData, setQueueData] = useState<QueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [jobIdInput, setJobIdInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('processing');

  const fetchQueueStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getQueueStatus();
      if (response.success && response.data) {
        setQueueData(response.data);
      } else {
        setError(response.error ?? 'Failed to fetch queue status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch queue status');
      toast.error('Failed to fetch queue status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueueStatus();

    if (autoRefresh) {
      const interval = setInterval(fetchQueueStatus, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const handleCancelJob = async () => {
    if (!jobIdInput.trim()) {
      toast.error('Please enter a job ID');
      return;
    }

    try {
      const response = await apiService.cancelJob(jobIdInput.trim());
      if (response.success) {
        toast.success('Job cancelled successfully');
        setCancelDialogOpen(false);
        setJobIdInput('');
        fetchQueueStatus();
      } else {
        toast.error(response.error || 'Failed to cancel job');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to cancel job');
    }
  };

  const handleStopAll = async () => {
    if (!window.confirm('Are you sure you want to stop all jobs? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.stopAllJobs();
      if (response.success) {
        toast.success(`Stopped ${response.data?.total_cancelled ?? 0} job(s)`);
        fetchQueueStatus();
      } else {
        toast.error(response.error || 'Failed to stop all jobs');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to stop all jobs');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getStatusChip = (status: string) => {
    return (
      <Chip
        label={status.toUpperCase()}
        color={getStatusColor(status) as any}
        size="small"
      />
    );
  };

  const getFilteredJobs = (jobs: Job[]) => {
    if (!jobs) return [];
    
    switch (statusFilter) {
      case 'all':
        return jobs;
      case 'processing':
        return jobs.filter(job => 
          job.status === 'queued' || 
          job.status === 'running' || 
          job.status === 'Processing'
        );
      case 'success':
        return jobs.filter(job => 
          job.status === 'success' || 
          job.status === 'Finished'
        );
      case 'error':
        return jobs.filter(job => 
          job.status === 'error' || 
          job.status === 'Failed'
        );
      case 'cancelled':
        return jobs.filter(job => job.status === 'cancelled');
      default:
        return jobs;
    }
  };

  const handleStatusFilterChange = (event: React.SyntheticEvent, newValue: string) => {
    setStatusFilter(newValue);
  };

  if (loading && !queueData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Queue Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchQueueStatus}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color={autoRefresh ? 'primary' : 'inherit'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{ mr: 1 }}
          >
            Auto Refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Stop />}
            onClick={handleStopAll}
            disabled={!queueData?.aggregated.active_jobs && !queueData?.aggregated.queued_jobs}
          >
            Stop All Jobs
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Aggregated Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PlayArrow color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Active Jobs
                </Typography>
              </Box>
              <Typography variant="h4">{queueData?.aggregated.active_jobs ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Queue color="warning" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Queued Jobs
                </Typography>
              </Box>
              <Typography variant="h4">{queueData?.aggregated.queued_jobs ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Completed Jobs
                </Typography>
              </Box>
              <Typography variant="h4">{queueData?.aggregated.completed_jobs ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Queue color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Total Capacity
                </Typography>
              </Box>
              <Typography variant="h4">{queueData?.aggregated.total_capacity ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Service Status Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Service Status
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Active Jobs</TableCell>
                  <TableCell align="right">Queued Jobs</TableCell>
                  <TableCell align="right">Completed Jobs</TableCell>
                  <TableCell align="right">Capacity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queueData?.services.map((service) => (
                  <TableRow key={service.serviceUrl}>
                    <TableCell>{service.serviceUrl}</TableCell>
                    <TableCell>{getStatusChip(service.status)}</TableCell>
                    <TableCell align="right">
                      {service.data?.active_jobs ?? '-'}
                    </TableCell>
                    <TableCell align="right">
                      {service.data?.queued_jobs ?? '-'}
                    </TableCell>
                    <TableCell align="right">
                      {service.data?.completed_jobs ?? '-'}
                    </TableCell>
                    <TableCell align="right">
                      {service.data?.total_capacity ?? '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {queueData?.jobs && queueData.jobs.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Jobs List
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList color="action" />
                <Typography variant="body2" color="textSecondary">
                  {getFilteredJobs(queueData.jobs).length} of {queueData.jobs.length} jobs
                </Typography>
              </Box>
            </Box>
            
            {/* Status Filter Tabs */}
            <Tabs
              value={statusFilter}
              onChange={handleStatusFilterChange}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Processing" value="processing" />
              <Tab label="Success" value="success" />
              <Tab label="Error" value="error" />
              <Tab label="Cancelled" value="cancelled" />
              <Tab label="All" value="all" />
            </Tabs>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredJobs(queueData.jobs)
                    .sort((a, b) => {
                      // Sort by created_at, most recent first
                      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                      return dateB - dateA;
                    })
                    .map((job) => (
                      <TableRow key={job.job_id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {job.job_id.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.job_type === 'video_swap' ? 'Video' : job.job_type === 'image_swap' ? 'Image' : job.job_type}
                            size="small"
                            color={job.job_type === 'video_swap' ? 'secondary' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            size="small"
                            color={
                              job.status === 'success' || job.status === 'Finished'
                                ? 'success'
                                : job.status === 'error' || job.status === 'Failed'
                                ? 'error'
                                : job.status === 'queued'
                                ? 'default'
                                : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: '100px' }}>
                              <LinearProgress
                                variant="determinate"
                                value={(job.progress?.progress ?? 0) * 100}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ minWidth: '40px' }}>
                              {((job.progress?.progress ?? 0) * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {job.error ? (
                            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                              {job.error}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {job.progress?.message ?? '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {job.created_at
                              ? new Date(job.created_at).toLocaleString()
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setJobIdInput(job.job_id);
                              setCancelDialogOpen(true);
                            }}
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Job Management */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Job Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel Job by ID
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Cancel Job Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Job</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Job ID"
            fullWidth
            variant="outlined"
            value={jobIdInput}
            onChange={(e) => setJobIdInput(e.target.value)}
            placeholder="Enter job ID to cancel"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCancelJob} color="error" variant="contained">
            Cancel Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* Last Updated */}
      {queueData?.timestamp && (
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Typography variant="caption" color="textSecondary">
            Last updated: {new Date(queueData.timestamp).toLocaleString()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QueueManagement;

